/**
 * RFQ Locked Modal
 * =================
 * 
 * Displays a warning when user tries to access an RFQ that is locked by another user.
 * Shows who has the lock and when they started editing.
 */

import React from 'react';
import { LockInfo, formatLockTime } from '../../../utils/rfqLock';
import './RfqLockedModal.scss';

interface RfqLockedModalProps {
    isOpen: boolean;
    lockInfo: LockInfo | null;
    onClose: () => void;
    onGoBack: () => void;
}

const RfqLockedModal: React.FC<RfqLockedModalProps> = ({
    isOpen,
    lockInfo,
    onClose,
    onGoBack
}) => {
    if (!isOpen || !lockInfo) return null;

    const lockedTimeAgo = lockInfo.locked_at ? formatLockTime(lockInfo.locked_at) : 'recently';

    return (
        <div className="rfq-locked-modal-overlay" onClick={onClose}>
            <div className="rfq-locked-modal" onClick={(e) => e.stopPropagation()}>
                <div className="rfq-locked-modal__icon">
                    <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z" stroke="currentColor" strokeWidth="2" />
                        <path d="M12 8V12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                        <circle cx="12" cy="16" r="1" fill="currentColor" />
                    </svg>
                </div>

                <h2 className="rfq-locked-modal__title">RFQ Locked</h2>

                <p className="rfq-locked-modal__message">
                    This RFQ is currently being edited by another user.
                </p>

                <div className="rfq-locked-modal__info">
                    <div className="rfq-locked-modal__info-row">
                        <span className="rfq-locked-modal__label">Locked by:</span>
                        <span className="rfq-locked-modal__value">{lockInfo.user_name}</span>
                    </div>
                    <div className="rfq-locked-modal__info-row">
                        <span className="rfq-locked-modal__label">Started:</span>
                        <span className="rfq-locked-modal__value">{lockedTimeAgo}</span>
                    </div>
                </div>

                <p className="rfq-locked-modal__hint">
                    Please wait until they finish or contact them directly.
                </p>

                <div className="rfq-locked-modal__actions">
                    <button
                        className="rfq-locked-modal__button rfq-locked-modal__button--primary"
                        onClick={onGoBack}
                    >
                        Go Back
                    </button>
                </div>
            </div>
        </div>
    );
};

export default RfqLockedModal;
